/**
 * GeoJSON Converter for LoadSEER Geometries
 * Converts LoadSEER API geometry responses to Kepler.gl-compatible GeoJSON format.
 */

import type { NodeGeometryFeature, GeoJSONGeometry } from '../types';

export interface KeplerGeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: string;
    coordinates: number[] | number[][] | number[][][] | number[][][][];
  };
  properties: Record<string, unknown>;
}

export interface KeplerGeoJSONFeatureCollection {
  type: 'FeatureCollection';
  features: KeplerGeoJSONFeature[];
}

/**
 * Converts LoadSEER geometry features to GeoJSON FeatureCollection
 *
 * @param features - LoadSEER NodeGeometryFeature array (already in [lng, lat] format)
 * @param nodeMetadata - Metadata to attach to each feature's properties
 * @returns FeatureCollection compatible with Kepler.gl's processRowObject
 *
 * Note: LoadSEER API returns coordinates in [longitude, latitude] order,
 * which matches Kepler.gl's requirements. No coordinate transformation needed.
 */
/**
 * Transform node geometry attributes according to display requirements:
 * 1. Calculate kw_total = kwperunit * numunits (if both present and valid)
 * 2. Remove all kwh-related fields (kwhperunit, kwh, kwhannual)
 * 3. Extract latitude/longitude from Point geometry coordinates
 * 4. Remove legacy Point object from attributes
 */
function transformAttributes(
  attributes: Record<string, unknown>,
  geometry: GeoJSONGeometry
): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  // Filter out kwh fields and Point object
  for (const [key, value] of Object.entries(attributes)) {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('kwh') || lowerKey === 'point') {
      continue; // Skip kwh fields and Point object
    }
    transformed[key] = value;
  }

  // Calculate kw_total if both kwperunit and numunits are valid numbers > 0
  const kwperunit = typeof attributes.kwperunit === 'number' ? attributes.kwperunit : null;
  const numunits = typeof attributes.numunits === 'number' ? attributes.numunits : null;

  if (kwperunit !== null && numunits !== null && kwperunit > 0 && numunits > 0) {
    transformed.kw_total = kwperunit * numunits;
  }

  // Extract lat/long from Point geometry coordinates
  if (geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
    const coords = geometry.coordinates as number[];
    if (coords.length >= 2) {
      transformed.longitude = coords[0]; // GeoJSON uses [lng, lat] order
      transformed.latitude = coords[1];
    }
  }

  return transformed;
}

export function convertToGeoJSON(
  features: NodeGeometryFeature[],
  nodeMetadata: {
    lsid: string;
    name: string;
    nodeType: string;
    externalId: string | null;
    parentNodeName?: string;
  }
): KeplerGeoJSONFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.map(feature => {
      const transformedAttrs = transformAttributes(feature.attributes, feature.geometry);

      return {
        type: 'Feature',
        geometry: {
          type: feature.geometry.type,
          coordinates: feature.geometry.coordinates,
        },
        properties: {
          ...transformedAttrs,
          // Add node metadata
          lsid: nodeMetadata.lsid,
          nodeName: nodeMetadata.name,
          nodeType: nodeMetadata.nodeType,
          externalId: nodeMetadata.externalId,
          // Add parent node name if this is a descendant
          ...(nodeMetadata.parentNodeName && { parentNodeName: nodeMetadata.parentNodeName }),
        },
      };
    }),
  };
}

/**
 * Converts multiple node results to a single GeoJSON FeatureCollection
 *
 * @param nodeResults - Array of nodes with their geometry features
 * @returns Combined FeatureCollection for all nodes
 *
 * This function combines geometries from multiple LoadSEER nodes into a single
 * dataset. Kepler.gl will:
 * 1. Process this via processRowObject (handles GeoJSON FeatureCollections)
 * 2. Generate a unique dataset ID via generateHashId(4)
 * 3. Assign a color from the preset palette via getNewDatasetColor()
 * 4. Create fields from properties and _geojson geometry field
 *
 * Reference: keplergl/kepler.gl src/table/src/dataset-utils.ts
 */
export function convertMultipleNodesToGeoJSON(
  nodeResults: Array<{
    lsid: string;
    name: string;
    nodeType: string;
    externalId: string | null;
    features?: NodeGeometryFeature[];
    parentNodeName?: string;
  }>
): KeplerGeoJSONFeatureCollection {
  const allFeatures: KeplerGeoJSONFeature[] = [];

  nodeResults.forEach(node => {
    if (!node.features || node.features.length === 0) {
      return;
    }

    const nodeFeatures = convertToGeoJSON(node.features, {
      lsid: node.lsid,
      name: node.name,
      nodeType: node.nodeType,
      externalId: node.externalId,
      parentNodeName: node.parentNodeName,
    });
    allFeatures.push(...nodeFeatures.features);
  });

  return {
    type: 'FeatureCollection',
    features: allFeatures,
  };
}

/**
 * Converts GeoJSON to a Blob for Kepler.gl ingestion
 */
export function geoJSONToBlob(
  geoJSON: KeplerGeoJSONFeatureCollection,
  fileName = 'loadseer-data.geojson'
): File {
  const jsonString = JSON.stringify(geoJSON, null, 2);
  const blob = new Blob([jsonString], { type: 'application/geo+json' });
  return new File([blob], fileName, { type: 'application/geo+json' });
}

/**
 * Calculates the bounding box for a set of features
 */
export function calculateBoundingBox(features: NodeGeometryFeature[]): {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} | null {
  if (features.length === 0) {
    return null;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;

  features.forEach(feature => {
    const { type, coordinates } = feature.geometry;

    const updateBounds = (lon: number, lat: number) => {
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
    };

    if (type === 'Point') {
      const [lon, lat] = coordinates as number[];
      updateBounds(lon, lat);
    } else if (type === 'LineString' || type === 'MultiPoint') {
      (coordinates as number[][]).forEach(([lon, lat]) => updateBounds(lon, lat));
    } else if (type === 'Polygon' || type === 'MultiLineString') {
      (coordinates as number[][][]).forEach(ring => {
        ring.forEach(([lon, lat]) => updateBounds(lon, lat));
      });
    } else if (type === 'MultiPolygon') {
      (coordinates as number[][][][]).forEach(polygon => {
        polygon.forEach(ring => {
          ring.forEach(([lon, lat]) => updateBounds(lon, lat));
        });
      });
    } else {
      if (import.meta.env.DEV) {
        console.warn(`Unsupported geometry type for bounding box calculation: ${type}`);
      }
    }
  });

  return { minLat, maxLat, minLon, maxLon };
}
